import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/solid';
import PublicNavbar from './common/PublicNavbar';
import PublicFooter from './common/PublicFooter';
import { useAuth } from '../contexts/AuthContext';
import { stripeAPI } from '../services/api';

const CheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [countdown, setCountdown] = useState(8);

  // Verify the checkout session with the backend and refresh user data
  useEffect(() => {
    const verifyAndRefresh = async () => {
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        try {
          await stripeAPI.verifyCheckout(sessionId);
        } catch (e) {
          console.error('Checkout verification:', e);
        }
      }
      await refreshUser();
    };
    verifyAndRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Refresh user data one more time before redirect, then navigate
          refreshUser().then(() => {
            const user = localStorage.getItem('user');
            if (user) {
              const parsed = JSON.parse(user);
              if (parsed.is_company) {
                navigate('/company/dashboard');
              } else {
                navigate('/login');
              }
            } else {
              navigate('/login');
            }
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate, refreshUser]);

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <PublicNavbar solid />

      <section style={{
        background: 'linear-gradient(135deg, #059669 0%, #10b981 40%, #34d399 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '80px',
      }}>
        <Container>
          <div style={{
            maxWidth: '520px',
            margin: '0 auto',
            background: '#fff',
            borderRadius: '20px',
            padding: '3rem 2.5rem',
            textAlign: 'center',
            boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #059669, #10b981)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
            }}>
              <CheckCircleIcon style={{ width: '40px', height: '40px', color: '#fff' }} />
            </div>

            <h2 style={{
              fontWeight: 800,
              fontSize: '1.75rem',
              color: '#0f172a',
              marginBottom: '0.75rem',
              letterSpacing: '-0.02em',
            }}>
              Subscription Activated!
            </h2>

            <p style={{
              color: '#64748b',
              fontSize: '1rem',
              lineHeight: 1.6,
              marginBottom: '2rem',
            }}>
              Your payment was successful and your subscription is now active.
              You have full access to all features included in your plan.
            </p>

            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '1rem 1.25rem',
              marginBottom: '2rem',
            }}>
              <p style={{ color: '#166534', fontSize: '0.9rem', margin: 0, fontWeight: 500 }}>
                You can manage your subscription, update payment methods, or download invoices from your dashboard settings at any time.
              </p>
            </div>

            <button
              onClick={() => navigate('/company/dashboard')}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #059669, #10b981)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 24px',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(5,150,105,0.3)',
                transition: 'transform 0.15s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'none'}
            >
              <RocketLaunchIcon style={{ width: '18px', height: '18px' }} />
              Go to Dashboard
            </button>

            <p style={{
              color: '#94a3b8',
              fontSize: '0.8rem',
              marginTop: '1rem',
            }}>
              Redirecting automatically in {countdown} seconds...
            </p>
          </div>
        </Container>
      </section>

      <PublicFooter />
    </div>
  );
};

export default CheckoutSuccess;
